import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import s from './ApiKeysPage.module.css';
import { Card } from '@/components/primitives/Card';
import { Spinner } from '@/components/primitives/Spinner';
import { StatusPill } from '@/components/primitives/StatusPill';
import { useTsApiKeys } from '@/api/tailscale/useTailscale';
import { relativeTime } from '@/lib/relativeTime';
/**
 * Headscale's own admin API keys (the ones our backend uses to talk
 * upstream, plus any others created via `headscale apikeys create`).
 *
 * Headscale only returns the prefix (~7 chars) — the rest is hashed
 * server-side. Phase 1 = read-only list. Create / expire land in
 * Phase 3 with the "show secret once" UX (since `create` returns the
 * full plaintext key exactly once).
 */
export function ApiKeysPage() {
    const q = useTsApiKeys();
    const keys = q.data?.apiKeys ?? [];
    const now = Date.now();
    // Sort: active first, then by expiration date desc (freshest at the
    // top within each group).
    const sorted = [...keys].sort((a, b) => {
        const aExp = new Date(a.expiration).getTime();
        const bExp = new Date(b.expiration).getTime();
        const aLive = aExp > now;
        const bLive = bExp > now;
        if (aLive !== bLive)
            return aLive ? -1 : 1;
        return bExp - aExp;
    });
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: s.header, children: _jsxs("div", { children: [_jsx("h1", { className: s.h1, children: "API keys" }), _jsx("div", { className: s.h1Sub, children: "Headscale-side admin keys. Used by tooling and our panel. Only the prefix is recoverable \u2014 full keys are hashed." })] }) }), q.isLoading && (_jsx(Card, { children: _jsxs("div", { className: s.placeholder, children: [_jsx(Spinner, { size: 14 }), " Loading\u2026"] }) })), q.error && (_jsx(Card, { children: _jsx("div", { className: s.errBlock, children: q.error.message ?? 'Failed to load API keys' }) })), !q.isLoading && sorted.length === 0 && (_jsx(Card, { children: _jsx("div", { className: s.placeholder, children: "No API keys yet." }) })), _jsxs(Card, { className: s.list, children: [_jsxs("div", { className: s.rowHead, children: [_jsx("div", { children: "Prefix" }), _jsx("div", { children: "Created" }), _jsx("div", { children: "Expires" }), _jsx("div", { children: "Last seen" }), _jsx("div", { children: "State" })] }), sorted.map((k) => {
                        const exp = new Date(k.expiration).getTime();
                        const expired = exp <= now;
                        const expSoon = !expired && exp - now < 7 * 24 * 3600 * 1000;
                        const state = expired
                            ? { label: 'expired', tone: 'fail' }
                            : expSoon
                                ? { label: 'expires soon', tone: 'warn' }
                                : { label: 'active', tone: 'ok' };
                        return (_jsxs("div", { className: s.row, children: [_jsxs("div", { className: s.mono, children: [k.prefix, "\u2026"] }), _jsx("div", { className: s.cell, children: relativeTime(k.createdAt) }), _jsx("div", { className: s.cell, children: relativeTime(k.expiration) }), _jsx("div", { className: s.cell, children: k.lastSeen ? (relativeTime(k.lastSeen)) : (_jsx("span", { className: s.muted, children: "never" })) }), _jsx("div", { children: _jsx(StatusPill, { tone: state.tone, children: state.label }) })] }, k.id));
                    })] })] }));
}
